import { Cell, Pie, PieChart, ResponsiveContainer } from 'recharts';
import { strategyReport } from '@/utils/strategyReport';

type Tone = 'loss' | 'profit' | 'muted';

interface CalloutItem {
  label: string;
  value: string;
  tone: Tone;
}

const dotClass: Record<Tone, string> = {
  loss: 'bg-loss-500',
  profit: 'bg-profit-500',
  muted: 'bg-foreground-300',
};

const valueClass: Record<Tone, string> = {
  loss: 'text-loss-500',
  profit: 'text-profit-600',
  muted: 'text-foreground-500',
};

const ringData = [
  { name: 'Gross Loss', value: Math.abs(strategyReport.grossLoss) },
  { name: 'Gross Profit', value: strategyReport.grossProfit },
];

const leftCallouts: CalloutItem[] = [
  { label: 'Gross Loss', value: strategyReport.grossLossText, tone: 'loss' },
  { label: 'Swaps', value: strategyReport.swapsText, tone: 'muted' },
  { label: 'Commissions', value: strategyReport.commissionsText, tone: 'muted' },
];

const rightCallouts: CalloutItem[] = [
  { label: 'Gross Profit', value: strategyReport.grossProfitText, tone: 'profit' },
  { label: 'Dividends', value: strategyReport.dividendsCalloutText, tone: 'muted' },
];

const allCallouts = [...leftCallouts, ...rightCallouts];

function Callout({ item }: { item: CalloutItem }) {
  return (
    <div className="flex items-start gap-2">
      <span className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${dotClass[item.tone]}`} />
      <div>
        <p className="text-[12px] text-foreground-500 whitespace-nowrap">{item.label}</p>
        <p
          className={`mt-0.5 font-data text-[14px] font-semibold whitespace-nowrap ${valueClass[item.tone]}`}
        >
          {item.value}
        </p>
      </div>
    </div>
  );
}

function Ring({ sizeClass }: { sizeClass: string }) {
  return (
    <div className={`relative shrink-0 ${sizeClass}`}>
      <ResponsiveContainer width="100%" height="100%">
        <PieChart>
          <Pie
            data={ringData}
            dataKey="value"
            innerRadius="68%"
            outerRadius="100%"
            startAngle={90}
            endAngle={-270}
            paddingAngle={2}
            stroke="none"
          >
            <Cell fill="oklch(var(--loss-500))" />
            <Cell fill="oklch(var(--profit-500))" />
          </Pie>
        </PieChart>
      </ResponsiveContainer>

      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        <span className="font-data text-[11px] tracking-[0.16em] text-foreground-400">TOTAL</span>
        <span
          className={`mt-1 font-data text-[20px] font-semibold ${
            strategyReport.isProfit ? 'text-profit-600' : 'text-loss-500'
          }`}
        >
          {strategyReport.totalText}
        </span>
      </div>
    </div>
  );
}

// The "Summary" donut: gross profit vs gross loss around a TOTAL hub, with the
// ledger breakdown called out on both sides (or stacked below on small screens).
function SummaryRing() {
  return (
    <div>
      <div className="hidden items-center justify-center gap-5 sm:flex md:gap-7">
        <div className="flex flex-col justify-center gap-7">
          {leftCallouts.map((item) => (
            <Callout key={item.label} item={item} />
          ))}
        </div>

        <Ring sizeClass="h-[180px] w-[180px] lg:h-[200px] lg:w-[200px]" />

        <div className="flex flex-col justify-center gap-7">
          {rightCallouts.map((item) => (
            <Callout key={item.label} item={item} />
          ))}
        </div>
      </div>

      <div className="flex flex-col items-center gap-6 sm:hidden">
        <Ring sizeClass="h-[180px] w-[180px]" />
        <div className="grid w-full grid-cols-2 gap-x-5 gap-y-4">
          {allCallouts.map((item) => (
            <Callout key={item.label} item={item} />
          ))}
        </div>
      </div>
    </div>
  );
}

export default SummaryRing;